import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'
import Pusher from 'pusher-js'
import Echo, { Channel } from 'laravel-echo'
import wmAlert from '../helpers/wmAlerts'
import { makeChannelName } from '../helpers/stringUtils'

declare global {
    interface Window {
        Pusher: typeof Pusher
        Echo: Echo
        user: any
        pusherKey: string
        cluster: string
    }
}

interface PushListenerData {
    channel: Channel | null
    mediaChannel: Channel | null
}

export default defineComponent({
    name: 'PushListener',
    data(): PushListenerData {
        return {
            channel: null,
            mediaChannel: null,
        }
    },

    computed: {
        document(): any {
            return this.$store.state.document as any
        },
        ...mapGetters({
            mainDocument: 'document/mainDocument',
            documentModel: 'document/documentModel',
        }),
        mediaChannelName(): string {
            return makeChannelName(`document.media.${this.document.id}`)
        },
    },

    mounted() {
        this.initializeEcho()

        this.listenChannel()

        document.addEventListener('logIn', () => {
            this.listenChannel()
        })
    },

    methods: {
        initializeEcho() {
            window.Pusher = Pusher

            window.Echo = new Echo({
                broadcaster: 'pusher',
                key: window.pusherKey,
                cluster: window.cluster,
                disableStats: true,
                forceTLS: false,
            })
        },

        listenChannel() {
            if (!window.user || !window.user.email) return

            this.channel = window.Echo.channel(makeChannelName(`user.${window.user.email}`))

            this.channel.listen('LogoutEvent', () => {
                window.onbeforeunload = null
                window.location.href = '/account/logout'
            })

            this.channel.listen('SubscriptionEvent', () => {
                this.$store.dispatch('subscription/runSubscriptionShim')
            })

            this.channel.listen('WorkingDocumentEvent', async (response: any) => {
                if (this.documentModel.is_published) return

                if (this.checkWorkingDocument(response) && location.href.includes(response.documentId)) {
                    const userResponse = await wmAlert.info({
                        title: '<div class="mx-4">This document has been updated in a different tab.</div>',
                        html: '<div class="mx-4">Please refresh this page to show the current version</div>',
                        confirmButtonText: 'Refresh',
                        showDenyButton: false,
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    })

                    if (userResponse.isConfirmed) location.reload()
                }
            })
        },

        listenForMedia() {
            this.mediaChannel = window.Echo.channel(this.mediaChannelName)

            this.mediaChannel.listen('DocumentMediaHasBeenAdded', async () => {
                try {
                    await this.$store.dispatch('document/loadDocumentMedia')
                } catch (error) {
                    await this.$store.dispatch('document/setApiCallFeedback', 'There was a problem loading this document.')
                    await this.$modals.open('error_handling')
                }
            })
        },

        leaveMediaChannel() {
            if (this.mediaChannel) {
                window.Echo.leaveChannel(this.mediaChannelName)
            }
        },

        checkWorkingDocument(pusherResponse: any): boolean {
            if (pusherResponse.documentType !== 'worksheets') return false

            return pusherResponse.documentToken !== this.mainDocument
        },
    },
})
